/* Playful status lines while a monster reads or grades, in the spirit of Claude Code's spinner words.
   They sit above the plain message, which stays the real explanation (and the one screen readers hear).
   Each line changes every few seconds, counted from when it appeared, until it leaves the page.
   With reduced motion the first line stays put. */

export type QuipTask = "reading" | "checking" | "key";

const QUIPS: Record<QuipTask, string[]> = {
  reading: [
    "Squinting at sevens", "Deciphering doodles", "Untangling squiggles", "Telling 1s from 7s", "Peering at tiny digits",
    "Spotting backwards 3s", "Reading around eraser smudges", "Following the arrows", "Decoding pencil scratches", "Finding the answer box",
    "Turning the page sideways", "Admiring the bubble letters", "Telling 0s from 6s", "Finding where the 4 ends",
    "Following the number line", "Reading the tiny carry digits", "Squinting past the doodles", "Counting the tally marks",
    "Spotting the circled answer", "Unscrunching the worksheet", "Snacking on a stray pencil",
  ],
  checking: [
    "Carrying the one", "Crunching numbers", "Borrowing from the tens", "Lining up the decimals", "Counting on fingers",
    "Sharpening the red pencil", "Drawing check marks", "Hunting for slip-ups", "Double-checking sums", "Polishing gold stars",
    "Regrouping the tens", "Lining up the place values", "Checking the remainders", "Adding it up backwards", "Counting by fives",
    "Comparing fractions", "Measuring with a ruler", "Stamping the stickers", "Looking for the aha", "Giving partial credit",
    "Writing \"Great job!\"", "Tallying the right answers", "Asking the calculator nicely", "Consulting the math owl", "Bribing the decimals",
  ],
  key: [
    "Solving every problem", "Showing the work", "Writing the answer key", "Checking the key twice", "Carrying the one",
    "Counting the questions", "Sharpening the pencil",
    "Reading every question twice", "Working out the fractions", "Drawing the number lines", "Doing the long division",
    "Making a clean copy", "Putting the pencil behind the ear", "Negotiating with long division",
  ],
};
// A few each monster says only about themself
const OWN: Record<string, string[]> = {
  Fiona: ["Twirling a pencil in her paw", "Swishing her tail thoughtfully", "Fluffing her mane", "Tapping her claw on the desk", "Humming a times-table tune"],
  Kraken: [
    "Counting on all eight tentacles", "Inking the margins", "Talking to his parrot fish", "Holding eight pencils at once",
    "Blowing thoughtful bubbles", "Suction-cupping the page flat", "Checking every answer eight times",
  ],
};
const EVERY_MS = 2800;

const pick = (list: string[], not: string): string => {
  const rest = list.length > 1 ? list.filter(q => q !== not) : list;
  return rest[Math.floor(Math.random() * rest.length)] ?? "";
};
const isTask = (t: string | undefined): t is QuipTask => t !== undefined && Object.hasOwn(QUIPS, t);
const listFor = (task: QuipTask, monster: string): string[] => [...QUIPS[task], ...(OWN[monster] ?? [])];

/** Markup for a quip line; mountQuips keeps it changing. */
export const quipLine = (task: QuipTask, monster: string): string =>
  `<p class="quip" data-quip="${task}" data-monster-name="${monster}" aria-hidden="true">${pick(listFor(task, monster), "")}…</p>`;

export function mountQuips(root: HTMLElement = document.body): void {
  const still = matchMedia("(prefers-reduced-motion: reduce)");
  const started = new WeakSet<HTMLElement>();

  // one timer per line, started when it appears, so a fresh line is never swapped out straight away
  const start = (el: HTMLElement): void => {
    started.add(el);
    const task = el.dataset.quip;
    if (!isTask(task)) return;
    const list = listFor(task, el.dataset.monsterName ?? "");
    const timer = setInterval(() => {
      if (!el.isConnected) { clearInterval(timer); return; }
      if (still.matches) return;
      el.textContent = `${pick(list, (el.textContent ?? "").replace(/…$/, ""))}…`;
      // restart the fade-in
      el.classList.remove("swap"); void el.offsetWidth; el.classList.add("swap");
    }, EVERY_MS);
  };

  const scan = (): void => root.querySelectorAll<HTMLElement>(".quip[data-quip]").forEach(el => { if (!started.has(el)) start(el); });
  new MutationObserver(scan).observe(root, { childList: true, subtree: true });
  scan();
}
